/**
 * Cross-platform haptic feedback.
 *
 * Web: no-op (modern browsers expose `navigator.vibrate` but its behaviour
 * is inconsistent and users rarely expect vibration on desktop).
 *
 * Native: lazy-imports `@capacitor/haptics` so the web bundle stays lean
 * and jsdom tests don't need to mock the plugin.
 *
 * Keep callers simple — they should say "this is a success" or "this is
 * a tap", not pick an impact style themselves.
 */

import { isNative } from './index'

export type ImpactStyle = 'light' | 'medium' | 'heavy'
export type NotificationType = 'success' | 'warning' | 'error'

async function loadPlugin() {
  try {
    return await import(/* @vite-ignore */ '@capacitor/haptics')
  } catch {
    return null
  }
}

export async function impact(style: ImpactStyle = 'medium'): Promise<void> {
  if (!isNative()) return
  const mod = await loadPlugin()
  if (!mod) return
  try {
    const styleEnum = mod.ImpactStyle ?? { Light: 'LIGHT', Medium: 'MEDIUM', Heavy: 'HEAVY' }
    const styleValue = style === 'light'
      ? styleEnum.Light
      : style === 'heavy'
      ? styleEnum.Heavy
      : styleEnum.Medium
    await mod.Haptics.impact({ style: styleValue })
  } catch {
    // Plugin returned an error on this device — swallow; haptics are a
    // non-critical affordance.
  }
}

export async function notify(type: NotificationType = 'success'): Promise<void> {
  if (!isNative()) return
  const mod = await loadPlugin()
  if (!mod) return
  try {
    const typeEnum = mod.NotificationType ?? { Success: 'SUCCESS', Warning: 'WARNING', Error: 'ERROR' }
    const typeValue = type === 'warning'
      ? typeEnum.Warning
      : type === 'error'
      ? typeEnum.Error
      : typeEnum.Success
    await mod.Haptics.notification({ type: typeValue })
  } catch {
    /* non-critical */
  }
}

export async function selection(): Promise<void> {
  if (!isNative()) return
  const mod = await loadPlugin()
  if (!mod) return
  try {
    await mod.Haptics.selectionStart()
    await mod.Haptics.selectionEnd()
  } catch {
    /* non-critical */
  }
}
